import { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import ArticleLayout from "./components/ArticleLayout";
import { getArticleBySlug } from "@/mocks/articles";
import { useJsonLd } from "@/hooks/useJsonLd";
import { getSiteUrl } from "@/utils/seo";

interface ArticleContentItem {
  type: string;
  text?: string;
  title?: string;
  items?: (string | Record<string, unknown>)[];
}

function extractFaqItems(content: ArticleContentItem[]) {
  for (const block of content) {
    if (block.type === "faq" && block.items) {
      return block.items.filter(
        (item): item is Record<string, unknown> => typeof item !== "string"
      );
    }
  }
  return [];
}

function buildArticleJsonLd(
  article: ReturnType<typeof getArticleBySlug>,
  siteUrl: string
) {
  if (!article) return null;

  const canonicalUrl = `${siteUrl}/artykul/${article.slug}`;
  const faqItems = extractFaqItems(article.content);

  const graphItems: Record<string, unknown>[] = [
    {
      "@type": "Article",
      headline: article.title,
      description: article.description,
      url: canonicalUrl,
      image: article.heroImage,
      inLanguage: "pl-PL",
      datePublished: "2026-07-21",
      dateModified: "2026-07-21",
      author: {
        "@type": "Organization",
        name: "Kakobuy Spreadsheet Polska",
        url: siteUrl,
      },
      publisher: {
        "@type": "Organization",
        name: "Kakobuy Spreadsheet Polska",
        url: siteUrl,
      },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Strona Główna",
          item: siteUrl,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: article.title,
          item: canonicalUrl,
        },
      ],
    },
  ];

  if (faqItems.length > 0) {
    graphItems.push({
      "@type": "FAQPage",
      mainEntity: faqItems.map((item) => ({
        "@type": "Question",
        name: (item as Record<string, unknown>).q || "",
        acceptedAnswer: {
          "@type": "Answer",
          text: (item as Record<string, unknown>).a || "",
        },
      })),
    });
  }

  return {
    "@context": "https://schema.org",
    "@graph": graphItems,
  };
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : null;
  const siteUrl = getSiteUrl();

  const articleJsonLd = useMemo(
    () => buildArticleJsonLd(article, siteUrl),
    [article, siteUrl]
  );

  useJsonLd(articleJsonLd);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center px-6">
          <div className="w-20 h-20 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-6">
            <i className="ri-file-search-line text-accent-600 text-3xl"></i>
          </div>
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            Artykuł nie znaleziony
          </h1>
          <p className="text-sm md:text-base text-foreground-600 mb-6">
            Przepraszamy, ale strona której szukasz nie istnieje.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full transition-all duration-200 cursor-pointer"
          >
            <i className="ri-home-4-line"></i>
            Powrót do strony głównej
          </Link>
        </div>
      </div>
    );
  }

  return (
    <ArticleLayout
      title={article.title}
      description={article.description}
      readingTime={article.readingTime}
      tag={article.tag}
      heroImage={article.heroImage}
      content={article.content}
    />
  );
}